/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner, Row, Col, Pagination } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaMoneyBillWave,
  FaUser,
  FaListAlt,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaSearch,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { addContribution, getAllUsers } from "../../../hooks/admin_api";

const AddContributionModal = ({ show, onHide, token, adminId, adminName }) => {
  const [formData, setFormData] = useState({
    member_id: "",
    amount: "",
    category: "100",
  });
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [error, setError] = useState("");
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const itemsPerPage = 20; // You can adjust this value

  // Fetch all users when modal opens or page changes
  useEffect(() => {
    if (show && token) {
      fetchAllUsers(currentPage);
    }
  }, [show, token, currentPage]);

  const fetchAllUsers = async (page = 1) => {
    try {
      setLoadingMembers(true);

      const response = await getAllUsers(token, page, itemsPerPage);

      if (response.status) {
        // Filter active users
        const activeUsers = response.users
          .filter((user) => user.is_active === "1" || user.is_active === 1)
          .map((user) => ({
            id: parseInt(user.user_id),
            user_id: user.user_id,
            full_name: user.full_name || "Unknown",
            email: user.email || "No email",
            phone: user.phone || "No phone",
            role: user.role || "user",
            is_active: user.is_active,
            custom_id: user.user_id,
          }));

        setMembers(activeUsers);

        // Set pagination info
        if (response.pagination) {
          setTotalPages(response.pagination.total_pages || response.pagination.totalPages || 1);
          setTotalUsers(response.pagination.total_users || response.pagination.total || 0);
          setPaginationInfo(response.pagination);
        } else {
          // If no pagination info, assume all users are loaded
          setTotalPages(1);
          setTotalUsers(activeUsers.length);
        }

        if (activeUsers.length === 0) {
          toast.info("No active users found on this page");
        }
      } else {
        toast.error(response.error || "Failed to load users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users: " + error.message);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Clear search when changing pages to avoid confusion
    setSearchTerm("");
    setSelectedMember(null);
    setFormData(prev => ({ ...prev, member_id: "" }));
  };

  // Handle previous page
  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Handle next page
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Filter members based on search term (only from current page)
  const filteredMembers = members.filter((member) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      (member.full_name &&
        member.full_name.toLowerCase().includes(searchLower)) ||
      (member.phone && member.phone.includes(searchTerm)) ||
      (member.user_id && member.user_id.toString().includes(searchTerm)) ||
      (member.email && member.email.toLowerCase().includes(searchLower))
    );
  });

  const amountOptions = [
    { value: "100", label: "₵100 (Standard)", color: "success" },
    { value: "50", label: "₵50 (Reduced)", color: "warning" },
    { value: "30", label: "₵30 (Basic)", color: "info" },
    { value: "other", label: "Other Amount", color: "secondary" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "member_id" && value) {
      const member = members.find((m) => m.id === value || m.user_id === value);
      setSelectedMember(member);
    }

    if (error) setError("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.member_id) {
      setError("Please select a member");
      return;
    }

    if (!formData.amount) {
      setError("Please select an amount");
      return;
    }

    // Handle custom amount
    let finalAmount = formData.amount;
    if (formData.amount === "other") {
      const customAmount = prompt("Enter custom amount (in GHS):");
      if (!customAmount || isNaN(customAmount) || customAmount <= 0) {
        setError("Please enter a valid amount");
        return;
      }
      finalAmount = customAmount;
    }

    setLoading(true);
    setError("");

    try {
      // Use the new admin function
      const res = await addContribution(
        formData.member_id,
        finalAmount,
        formData.category
      );

      console.log("Admin add contribution result:", res);

      if (res.status) {
        toast.success("✅ Contribution added successfully!");

        // Show notification details if available
        if (res.notification) {
          const smsStatus = res.notification.sms;
          if (smsStatus.any_sms_sent) {
            toast.info(
              `📱 SMS sent to ${smsStatus.member_sms_sent ? "member" : ""}${
                smsStatus.admin_sms_sent ? " and admin" : ""
              }`
            );
          }
        }

        // Reset form
        setFormData({
          member_id: "",
          amount: "",
          category: "100",
        });
        setSelectedMember(null);
        setSearchTerm("");
        setCurrentPage(1); // Reset to first page

        // Close modal
        onHide();
      } else {
        setError(res.message || "Failed to add contribution");
        toast.error("❌ " + (res.message || "Failed to add contribution"));
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Network error. Please try again.");
      toast.error("❌ Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      member_id: "",
      amount: "",
      category: "100",
    });
    setSelectedMember(null);
    setSearchTerm("");
    setError("");
    setCurrentPage(1);
    onHide();
  };

  // Render pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted small">
          Showing {members.length} of {totalUsers} total users
          {paginationInfo && ` (Page ${currentPage} of ${totalPages})`}
        </div>
        <Pagination className="mb-0">
          <Pagination.Prev
            onClick={handlePrevPage}
            disabled={currentPage === 1 || loadingMembers}
          />
          
          {startPage > 1 && (
            <>
              <Pagination.Item
                onClick={() => handlePageChange(1)}
                active={1 === currentPage}
                disabled={loadingMembers}
              >
                1
              </Pagination.Item>
              {startPage > 2 && <Pagination.Ellipsis disabled />}
            </>
          )}

          {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(
            (page) => (
              <Pagination.Item
                key={page}
                onClick={() => handlePageChange(page)}
                active={page === currentPage}
                disabled={loadingMembers}
              >
                {page}
              </Pagination.Item>
            )
          )}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
              <Pagination.Item
                onClick={() => handlePageChange(totalPages)}
                active={totalPages === currentPage}
                disabled={loadingMembers}
              >
                {totalPages}
              </Pagination.Item>
            </>
          )}

          <Pagination.Next
            onClick={handleNextPage}
            disabled={currentPage === totalPages || loadingMembers}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaMoneyBillWave className="me-2" />
          Add Contribution for Member
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {/* Search and Select Member */}
          <div className="mb-4">
            <Form.Label>
              <FaUser className="me-2" />
              <strong>Select Member *</strong>
            </Form.Label>

            {/* Search Bar with Clear Button */}
            <div className="input-group mb-3">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by name, phone, email, or ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                disabled={loadingMembers}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={handleClearSearch}
                  disabled={loadingMembers}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Member List */}
            <div
              className="member-list-container border rounded"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {loadingMembers ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 mb-0 text-muted">Loading users...</p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <Alert variant="warning" className="mb-0 m-2">
                  No users found. {searchTerm && "Try a different search term."}
                </Alert>
              ) : (
                <div className="list-group list-group-flush">
                  {filteredMembers.map((member) => (
                    <div
                      key={member.id || member.user_id}
                      className={`list-group-item list-group-item-action ${
                        formData.member_id === (member.id || member.user_id)
                          ? "active"
                          : ""
                      }`}
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          member_id: member.id || member.user_id,
                        }));
                        setSelectedMember(member);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{member.full_name}</h6>
                          <div className="small">
                            <FaIdCard className="me-1" /> ID:{" "}
                            {member.user_id || member.id}
                            <span className="mx-2">•</span>
                            <FaPhone className="me-1" /> {member.phone}
                          </div>
                          <small>
                            <FaEnvelope className="me-1" /> {member.email}
                          </small>
                        </div>
                        <span
                          className={`badge ${
                            member.role === "admin"
                              ? "bg-danger"
                              : member.role === "staff"
                              ? "bg-warning"
                              : "bg-success"
                          }`}
                        >
                          {member.role.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {!loadingMembers && members.length > 0 && renderPagination()}
          </div>

          {/* Selected Member Preview */}
          {selectedMember && (
            <Alert variant="info" className="mb-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Selected Member:</strong>
                  <div className="mt-2">
                    <p className="mb-1">
                      <FaUser className="me-2" />
                      <strong>Name:</strong> {selectedMember.full_name}
                    </p>
                    <p className="mb-1">
                      <FaPhone className="me-2" />
                      <strong>Phone:</strong> {selectedMember.phone}
                    </p>
                    <p className="mb-0">
                      <FaEnvelope className="me-2" />
                      <strong>Email:</strong> {selectedMember.email}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, member_id: "" }));
                    setSelectedMember(null);
                  }}
                >
                  Change
                </Button>
              </div>
            </Alert>
          )}

          {/* Amount Selection */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FaMoneyBillWave className="me-2" />
              <strong>Click round circle to Pick Amount *</strong>
            </Form.Label>
            <Row>
              {amountOptions.map((option) => (
                <Col key={option.value} xs={6} className="mb-2">
                  <Form.Check
                    type="radio"
                    id={`amount-${option.value}`}
                    name="amount"
                    value={option.value}
                    checked={formData.amount === option.value}
                    onChange={handleInputChange}
                    label={
                      <div className={`text-${option.color} fw-semibold`}>
                        {option.label}
                      </div>
                    }
                    className="p-3 border rounded"
                  />
                </Col>
              ))}
            </Row>
          </Form.Group>

          {/* Category */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FaListAlt className="me-2" />
              <strong>Category</strong>
            </Form.Label>
            <Form.Select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
            >
              <option value="100">₵100 Category</option>
              <option value="50">₵50 Category</option>
              <option value="30">₵30 Category</option>
            </Form.Select>
            <Form.Text className="text-muted">
              This determines the contribution category for reporting.
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !formData.member_id || !formData.amount}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <FaMoneyBillWave className="me-2" />
                Add Contribution
                {selectedMember &&
                  ` for ${selectedMember.full_name.split(" ")[0]}`}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddContributionModal;